/**
 * Wortweiser Diff für die Versionsansicht. Bewusst ohne Abhängigkeit: Der Text einer
 * Notiz ist klein genug für eine geradlinige LCS-Matrix, und ein eigenes kleines
 * Verfahren ist leichter zu prüfen als eine weitere Bibliothek.
 */

export type DiffPart = { type: 'equal' | 'added' | 'removed'; text: string };

function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((token) => token !== '');
}

const MAX_TOKENS = 6000;

export function diffWords(before: string, after: string): DiffPart[] {
  const a = tokenize(before);
  const b = tokenize(after);

  // Sehr lange Texte nicht Zeichen für Zeichen vergleichen — die Aussage bliebe gleich.
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return [
      { type: 'removed', text: before },
      { type: 'added', text: after },
    ];
  }

  const lengths: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lengths[i]![j]! = a[i] === b[j] ? lengths[i + 1]![j + 1]! + 1 : Math.max(lengths[i + 1]![j]!, lengths[i]![j + 1]!);
    }
  }

  const parts: DiffPart[] = [];
  const push = (type: DiffPart['type'], text: string): void => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.text += text;
    else parts.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('equal', a[i]!);
      i += 1;
      j += 1;
    } else if (lengths[i + 1]![j]! >= lengths[i]![j + 1]!) {
      push('removed', a[i]!);
      i += 1;
    } else {
      push('added', b[j]!);
      j += 1;
    }
  }
  while (i < a.length) push('removed', a[i++]!);
  while (j < b.length) push('added', b[j++]!);
  return parts;
}
